import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  uploadProfileDocument,
  getUploadProfileDocument,
  AuditDocument,
} from "../api/profile";
import {
  getDashboardStats,
  getDashboardUpcomingDues,
  getDashboardNextSteps,
  getDashboardMonthlySubmissions,
  getDashboardGrantDistribution,
  getDashboardTopPicks,
  getDashboardTrending,
  getDashboardAiInsight,
  createTeam,
  inviteToTeam,
  getTeamList,
  setActiveTeam,
} from "../api/dashboard";
import StatsRow from "../components/dashboard/cards/StatsRow";
import TotalGrantsByFeaturesCard from "../components/dashboard/cards/TotalGrantsByFeaturesCard";
import MonthlyBarsCard, {
  MonthlyBar,
} from "../components/dashboard/cards/MonthlyBarsCard";
import { useAuth } from "../contexts/AuthContext";
import Button from "../components/ui/Button";
import { toast } from "react-hot-toast";

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

type RootUser = {
  email?: string | null;
  profile?: {
    first_name?: string | null;
    last_name?: string | null;
    company_name?: string | null;
    profile_photo?: string | null;
  } | null;
};

type AiInsightItem = {
  metric?: string;
  insight?: string;
  value?: any;
};

type AiInsight = {
  greeting?: string;
  insights?: Array<AiInsightItem | string>;
  next_steps?: string[];
  positive_message?: string;
};

type TopPick = {
  id?: string;
  title?: string;
  url?: string;
  action?: string;
  match_score?: number;
  range_label?: string;
  badge?: string;
};

type UpcomingDue = {
  title?: string;
  action?: string;
  due_in_days?: number;
  saved_id?: number | string;
  saved_grant_id?: number | string;
  savedGrantId?: number | string;
};

type NextStep = {
  title?: string;
  action?: string;
  progress_pct?: number;
  saved_id?: number | string;
  saved_grant_id?: number | string;
  savedGrantId?: number | string;
};

type CommunityThread = {
  id: number;
  topic?: string;
  title?: string;
  replies?: number;
  likes?: number;
  action?: string;
};

type GrantFeature = { name: string; pct: number };

type MonthlySubmission = { month: string; count: number };

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

// function normaliseInsights(
//   raw: Array<AiInsightItem | string> | undefined
// ): Array<{ label?: string; text: string }> {
//   if (!Array.isArray(raw)) return [];
//   return raw
//     .map((item) => {
//       if (typeof item === "string") {
//         const trimmed = item.trim();
//         return trimmed ? { text: trimmed } : null;
//       }
//       if (item && typeof item === "object") {
//         const insightText = String(item.insight ?? "").trim();
//         const label = item.metric ? String(item.metric).trim() : undefined;
//         if (!insightText && !label) return null;
//         return { label, text: insightText || label || "" };
//       }
//       return null;
//     })
//     .filter((x): x is { label?: string; text: string } => x !== null);
// }
function normaliseInsights(
  raw: Array<AiInsightItem | string> | undefined
): Array<{ label?: string; text: string }> {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item) => {
      // Case 1: proper object with `insight` key
      if (item && typeof item === "object") {
        const text = String((item as any).insight ?? "").trim();
        return text ? { text } : null;
      }

      if (typeof item === "string") {
        const trimmed = item.trim();
        if (!trimmed) return null;

        // Case 2: stringified Python-style dict e.g. "{'insight': '...', 'matrix_values': {...}}"
        // Try to extract the insight value from the string
        const insightMatch = trimmed.match(/['"]insight['"]\s*:\s*['"]([\s\S]+?)['"]\s*,\s*['"]matrix/);
        if (insightMatch) {
          return { text: insightMatch[1].trim() };
        }

        // Case 2b: stringified dict but grab everything after 'insight': ' until closing quote before comma/end
        const looseMatch = trimmed.match(/['"]insight['"]\s*:\s*['"]([\s\S]+?)['"](?:\s*[,}]|$)/);
        if (looseMatch) {
          return { text: looseMatch[1].trim() };
        }

        // Case 3: plain string
        return { text: trimmed };
      }

      return null;
    })
    .filter((x): x is { label?: string; text: string } => x !== null); 
}

// ─────────────────────────────────────────────────────────────
// SKELETON COMPONENTS
// ─────────────────────────────────────────────────────────────

const SkeletonBox = ({ className = "" }: { className?: string }) => ( 
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

const SkeletonCard = ({ lines = 3 }: { lines?: number }) => (
  <div className="rounded-2xl border border-black/10 bg-white p-4 space-y-3">
    <SkeletonBox className="h-5 w-1/3" />
    <SkeletonBox className="h-4 w-2/3" />
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className="space-y-2 mt-3">
        <SkeletonBox className="h-4 w-full" />
        <SkeletonBox className="h-3 w-3/4" />
      </div>
    ))}
  </div>
);

const StatsRowSkeleton = () => (
  <div className="grid grid-cols-3 gap-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="rounded-2xl border border-black/10 bg-white p-4 space-y-2">
        <SkeletonBox className="h-4 w-1/2" />
        <SkeletonBox className="h-8 w-1/3" />
        <SkeletonBox className="h-3 w-2/3" />
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────
// SHARED UI PRIMITIVES
// ─────────────────────────────────────────────────────────────

const Card: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className = "",
  children,
}) => (
  <div className={`rounded-2xl border border-black/10 bg-white ${className}`}>
    {children}
  </div>
);

const Badge = ({
  text,
  tone = "default",
}: {
  text: string;
  tone?: "default" | "success" | "info";
}) => {
  const cls =
    tone === "success"
      ? "bg-[#C6FDDE] text-[#2E8B57]"
      : tone === "info"
        ? "bg-[#BCDCEE] text-[#0077B6]"
        : "bg-[#FFEFCF] text-[#F39C12]";
  return (
    <span className={`px-3 py-[6px] rounded-full text-[12px] font-medium ${cls}`}>
      {text}
    </span>
  );
};

function dueColor(days: number | null): string {
  if (days === null) return "#6B7280";
  if (days <= 3) return "#EF4444";
  if (days <= 7) return "#F59E0B";
  if (days <= 14) return "#F97316";
  return "#10B981";
}

function dueBg(days: number | null): string {
  if (days === null) return "bg-gray-100 text-gray-600";
  if (days <= 3) return "bg-red-50 text-red-700 border border-red-200";
  if (days <= 7) return "bg-amber-50 text-amber-700 border border-amber-200";
  if (days <= 14) return "bg-orange-50 text-orange-700 border border-orange-200";
  return "bg-green-50 text-green-700 border border-green-200";
}

// ─────────────────────────────────────────────────────────────
// DOCUMENT POPUP
// ─────────────────────────────────────────────────────────────

type DocumentPopupProps = {
  show: boolean;
  selectedDocName: string | null;
  pendingFile: File | null;
  uploading: boolean;
  onClose: () => void;
  onSubmit: (name: string, expiry: string) => void;
  onFileChange: (file: File) => void;
};

function DocumentPopup({
  show,
  selectedDocName,
  pendingFile,
  uploading,
  onClose,
  onSubmit,
  onFileChange,
}: DocumentPopupProps) {
  const [name, setName] = React.useState("document");
  const [expiry, setExpiry] = React.useState("");
  const modalFileInput = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    setName(selectedDocName || "document");
    setExpiry("");
  }, [selectedDocName]);

  if (!show) return null;

  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-[400px] space-y-4">
        <h2 className="text-lg font-semibold">Upload Document</h2>
        <div className="space-y-1">
          <label className="text-sm font-medium">Selected File</label>
          <div 
            title={pendingFile ? pendingFile.name : ""}
            className="border px-3 py-2 w-full truncate overflow-hidden horounded bg-gray-50">
            {pendingFile ? pendingFile.name : "No file selected"}
          </div>
          <button
            className="text-blue-600 underline text-sm mt-1"
            onClick={() => modalFileInput.current?.click()}
          >
            Change File
          </button>
          <input
            ref={modalFileInput}
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFileChange(f);
              e.target.value = "";
            }}
            accept="application/pdf,image/*"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Document Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="border px-3 py-2 w-full rounded"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Expiry Date</label>
          <input
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            min={todayStr}
            className="border px-3 py-2 w-full rounded"
          />
        </div>
        <div className="flex justify-end gap-3">
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSubmit(name, expiry)} disabled={uploading}>
            {uploading ? "Uploading…" : "Submit"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TEAM MEMBER MODAL
// ─────────────────────────────────────────────────────────────

type Team = { id: number; name: string; is_active?: boolean };

function TeamMemberModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "editor" | "viewer">("editor");
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamId, setTeamId] = useState<number | null>(null);
  const [teamName, setTeamName] = useState("");
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setEmail(""); setRole("editor"); setTeamName(""); setTeams([]); setTeamId(null);
    setMode("existing"); setSubmitting(false); setLoadingTeams(true);
    (async () => {
      try {
        const data = await getTeamList();
        const list: Team[] = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
        setTeams(list);
        if (list.length > 0) {
          const active = list.find((t) => t.is_active) || list[0];
          setTeamId(active?.id ?? null);
          setMode("existing");
        } else setMode("new");
      } catch (err: any) {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || err?.message || err?.response?.data?.detail || "Failed to load teams");
        setMode("new");
      } finally { setLoadingTeams(false); }
    })();
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Please enter email"); return; }
    try {
      setSubmitting(true);
      let finalTeamId = teamId;
      if (mode === "new" || !finalTeamId) {
        if (!teamName.trim()) { toast.error("Please enter team name"); setSubmitting(false); return; }
        const created = await createTeam(teamName.trim());
        const newId: number | undefined = (created as any)?.team_id ?? (created as any)?.id ?? (Array.isArray(created) ? (created as any)[0]?.id : undefined);
        if (!newId) throw new Error("Team created.");
        finalTeamId = newId;
        await setActiveTeam(newId);
      }
      if (!finalTeamId) throw new Error("No team selected");
      await inviteToTeam({ email: email.trim(), team_id: finalTeamId, role });
      toast.success("Team member invited");
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || err?.response?.data?.detail || err?.message || "Failed to add team member");
    } finally { setSubmitting(false); }
  };

  const showExistingToggle = teams.length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[#141414]">Add team member</h2>
          <button type="button" onClick={onClose} className="text-[#6B7280] hover:text-[#111827]">
            <span className="material-icons text-[20px]">close</span>
          </button>
        </div>
        <form className="space-y-4" onSubmit={handleSubmit}>
          {showExistingToggle && (
            <div className="flex gap-4 text-sm">
              {(["existing", "new"] as const).map((m) => (
                <button key={m} type="button"
                  className={`px-3 py-1 rounded-full border ${mode === m ? "border-[#0077B6] text-[#0077B6]" : "border-[#E5E7EB] text-[#4B5563]"}`}
                  onClick={() => setMode(m)} disabled={loadingTeams}>
                  {m === "existing" ? "Use existing team" : "Create new team"}
                </button>
              ))}
            </div>
          )}
          {mode === "existing" && teams.length > 0 && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-[#374151]">Select team</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#0077B6]"
                value={teamId ?? ""} disabled={loadingTeams}
                onChange={async (e) => {
                  const val = e.target.value ? Number(e.target.value) : null;
                  setTeamId(val);
                  if (val) { try { await setActiveTeam(val); } catch {} }
                }}>
                <option value="" disabled>Select team</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          {(mode === "new" || teams.length === 0) && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-[#374151]">Team name</label>
              <input type="text" className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#0077B6]"
                value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="e.g. Fundraising team" />
            </div>
          )}
          <div className="space-y-1">
            <label className="text-sm font-medium text-[#374151]">Member email</label>
            <input type="email" className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#0077B6]"
              value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-[#374151]">Role</label>
            <select className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#0077B6]"
              value={role} onChange={(e) => setRole(e.target.value as any)}>
              <option value="" disabled>Select role</option>
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting || loadingTeams}>
              {submitting ? "Sending invite…" : "Send invite"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// AI INSIGHTS CARD
// ─────────────────────────────────────────────────────────────

function AiInsightsCard({
  ai,
  aiLoading,
  aiErr,
  onRefresh,
}: {
  ai: AiInsight | null;
  aiLoading: boolean;
  aiErr: string | null;
  onRefresh: () => void;
}) {
  const normalisedInsights = normaliseInsights(ai?.insights);

  if (aiLoading) {
    return (
      <Card>
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <SkeletonBox className="h-5 w-28" />
            <SkeletonBox className="h-8 w-8 rounded" />
          </div>
          <SkeletonBox className="h-4 w-full" />
          <SkeletonBox className="h-4 w-5/6" />
          <SkeletonBox className="h-4 w-4/6" />
          <SkeletonBox className="h-10 w-full rounded-lg mt-2" />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="text-xl font-semibold text-[#141414] flex items-center gap-2">
            <span className="material-icons text-[20px] text-primary">lightbulb</span>
            AI Insights
          </div>
          <div className="flex items-center gap-2">
            {aiErr && <span className="text-xs text-red-600 max-w-[120px] truncate">{aiErr}</span>}
            <button onClick={onRefresh} disabled={aiLoading}
              className="h-8 w-8 rounded border border-[#E5E7EB] grid place-items-center hover:bg-[#F9FAFB] transition-colors"
              title="Refresh insights">
              <span className={`material-icons text-[18px] text-[#6B7280] ${aiLoading ? "animate-spin" : ""}`}>
                {aiLoading ? "hourglass_top" : "refresh"}
              </span>
            </button>
          </div>
        </div>
        <div className="mt-3 text-sm text-[#111]">
          {ai?.greeting && (
            <p className="font-medium text-[14px] mb-3 leading-snug">{ai.greeting}</p>
          )}
          {normalisedInsights.length > 0 && (
            <div className="mb-4">
              <div className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Highlights</div>
              <ul className="space-y-2">
                {normalisedInsights.slice(0, 4).map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] leading-snug">
                    <span className="mt-[5px] h-[7px] w-[7px] rounded-full bg-primary flex-shrink-0" />
                    <span>
                      {item.label && <span className="font-semibold text-[#141414]">{item.label}: </span>}
                      <span className="text-[#374151]">{item.text}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!!ai?.next_steps?.length && (
            <div className="mb-3">
              <div className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Suggested next steps</div>
              <ol className="list-decimal ml-4 space-y-1">
                {ai.next_steps.slice(0, 3).map((s, i) => (
                  <li key={i} className="text-[13px] leading-snug text-[#374151]">
                    {typeof s === "string" ? s : (s as any)?.title ?? JSON.stringify(s)}
                  </li>
                ))}
              </ol>
            </div>
          )}
          {ai?.positive_message && (
            <div className="mt-3 rounded-lg bg-[#C0EFC0] text-[#17331F] px-3 py-2 text-[13px] leading-snug">
              {ai.positive_message}
            </div>
          )}
          {!ai?.greeting && normalisedInsights.length === 0 && !ai?.next_steps?.length && !ai?.positive_message && (
            <div className="text-[13px] text-[#6B7280]">
              No insights available yet. Click refresh to try again.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// UPCOMING DUES CARD
// ─────────────────────────────────────────────────────────────

function UpcomingDuesCard({
  dues,
  loading,
  onNavigate,
}: {
  dues: UpcomingDue[] | null;
  loading: boolean;
  onNavigate: (savedId?: number | string) => void;
}) {
  const nav = useNavigate();

  if (loading) return <SkeletonCard lines={3} />;

  const items = dues?.slice(0, 3) || [];

  return (
    <Card>
      <div className="p-4">
        <div className="text-xl font-semibold text-[#141414]">Upcoming dues</div>
        <div className="text-[15px] text-[#6B7280]">Deadlines approaching — take action now</div>
        {items.length ? (
          <>
            <div className="mt-4 space-y-3">
              {items.map((d, i) => {
                const days = typeof d.due_in_days === "number" ? Math.max(0, d.due_in_days) : null;
                const savedId = (d as any).saved_id ?? (d as any).saved_grant_id ?? (d as any).savedGrantId ?? null;
                const barColor = dueColor(days);
                const isUrgent = days !== null && days <= 3;
                return (
                  <div key={i} className="rounded-lg border border-[#F3F4F6] bg-[#FAFAFA] p-3 flex items-start gap-3 hover:bg-[#F9FAFB] transition-colors">
                    <span className="h-full w-[5px] min-h-[48px] rounded-full flex-shrink-0 mt-0.5"
                      style={{ background: barColor }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-[#141414] truncate" title={d.title}>
                        {d.title || "Untitled Grant"}
                      </div>
                      <button
                        className="text-[12px] text-[#0077B6] hover:underline mt-0.5"
                        onClick={() => onNavigate(savedId)}
                      >
                        {d.action || "Start application"}
                      </button>
                    </div>
                    {days !== null && (
                      <div className={`flex-shrink-0 text-[11px] font-semibold px-2 py-1 rounded-full ${dueBg(days)}`}>
                        {days === 0 ? "Today!" : isUrgent ? `${days}d left` : `${days} days`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="pt-3 text-right">
              <button className="text-[#141414] text-[13px] font-medium hover:underline"
                onClick={() => nav("/dashboard/track")}>
                View all
              </button>
            </div>
          </>
        ) : (
          <div className="text-[13px] text-[#6B7280] py-4 flex items-center gap-2">
            <span className="material-icons text-[18px] text-green-500">check_circle</span>
            No upcoming dues
          </div>
        )}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────────────────────

export default function Dashboard() {
  const nav = useNavigate();
  const fileRef = useRef<HTMLInputElement | null>(null);

  // ── Per-section loading states ──
  const [statsLoading, setStatsLoading] = useState(true);
  const [duesLoading, setDuesLoading] = useState(true);
  const [stepsLoading, setStepsLoading] = useState(true);
  const [picksLoading, setPicksLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [communityLoading, setCommunityLoading] = useState(true);

  // ── Data states ──
  const [stats, setStats] = useState<{
    total_grants_applied: number;
    grants_applied_change_pct: number;
    total_grants_submitted: number;
    grants_submitted_change_pct: number;
    grants_in_progress: number;
    grants_in_progress_change_pct: number;
    total_grants_available: number;
    grants_available_change_pct: number;
  } | null>(null);

  const [upcomingDues, setUpcomingDues] = useState<UpcomingDue[] | null>(null);
  const [nextSteps, setNextSteps] = useState<NextStep[] | null>(null);
  const [topPicks, setTopPicks] = useState<TopPick[] | null>(null);
  const [monthlySubmissions, setMonthlySubmissions] = useState<MonthlySubmission[] | null>(null);
  const [grantDistribution, setGrantDistribution] = useState<{
    total_grants: number;
    features: GrantFeature[];
  } | null>(null);
  const [communityThreads, setCommunityThreads] = useState<CommunityThread[] | null>(null);

  const [ai, setAi] = useState<AiInsight | null>(null);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiErr, setAiErr] = useState<string | null>(null);

  const [documents, setDocuments] = useState<AuditDocument[]>([]);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showDocPopup, setShowDocPopup] = useState(false);
  const [selectedDocName, setSelectedDocName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const [showTeamModal, setShowTeamModal] = useState(false);

  // ── Fire all API calls in parallel on mount ──
  useEffect(() => {
    let alive = true;

    // 1. Stats
    getDashboardStats()
      .then((d) => { if (alive) { setStats(d); setStatsLoading(false); } })
      .catch(() => { if (alive) setStatsLoading(false); });

    // 2. Upcoming dues
    getDashboardUpcomingDues()
      .then((d) => { if (alive) { setUpcomingDues(Array.isArray(d) ? d : []); setDuesLoading(false); } })
      .catch(() => { if (alive) setDuesLoading(false); });

    // 3. Next steps
    getDashboardNextSteps()
      .then((d) => { if (alive) { setNextSteps(Array.isArray(d) ? d : []); setStepsLoading(false); } })
      .catch(() => { if (alive) setStepsLoading(false); });

    // 4. Top picks
    getDashboardTopPicks()
      .then((d) => { if (alive) { setTopPicks(Array.isArray(d) ? d : []); setPicksLoading(false); } })
      .catch(() => { if (alive) setPicksLoading(false); });

    // 5. Monthly submissions (chart)
    getDashboardMonthlySubmissions()
      .then((d) => { if (alive) { setMonthlySubmissions(Array.isArray(d) ? d : []); setChartsLoading(false); } })
      .catch(() => { if (alive) setChartsLoading(false); });

    // 6. Grant distribution (chart)
    getDashboardGrantDistribution()
      .then((d) => {
        if (alive) {
          setGrantDistribution({
            total_grants: Number(d?.total_grants ?? 0),
            features: Array.isArray(d?.features) ? d.features : [],
          });
        }
      })
      .catch(() => {});

    // 7. Trending / community
    getDashboardTrending()
      .then((d) => { if (alive) { setCommunityThreads(Array.isArray(d) ? d : []); setCommunityLoading(false); } })
      .catch(() => { if (alive) setCommunityLoading(false); });

    // 8. Profile documents
    getUploadProfileDocument()
      .then((d) => { if (alive) setDocuments(d || []); })
      .catch(() => {});

    return () => { alive = false; };
  }, []);

  const loadAi = async () => {
    try {
      setAiLoading(true);
      setAiErr(null);
      const data = await getDashboardAiInsight();
      setAi(data);
    } catch (e: any) {
      setAi(null);
      setAiErr(e?.response?.data?.error || e?.response?.data?.message ||  e?.response?.data?.detail || "Failed to load AI insights");
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => { loadAi(); }, []);

  // ── Derived ──
  const totalApplied = stats?.total_grants_applied ?? 0;
  const appliedDelta = stats?.grants_applied_change_pct ?? 0;
  const inProgress = stats?.grants_in_progress ?? 0;
  const inProgressDelta = stats?.grants_in_progress_change_pct ?? 0;
  const totalAvailable = stats?.total_grants_available ?? 0;
  const availableDelta = stats?.grants_available_change_pct ?? 0;

  const picks = topPicks?.slice(0, 3) || [];
  const steps = nextSteps?.slice(0, 3) || [];
  const threads = communityThreads?.slice(0, 3) || [];

  // ── Handlers ──
  const handleTopPickClick = (url?: string) => {
    const cleanUrl = url?.trim();
    if (!cleanUrl) { toast.error("No application link available for this grant yet."); return; }
    if (cleanUrl.startsWith("http://") || cleanUrl.startsWith("https://")) {
      window.open(cleanUrl, "_blank", "noopener,noreferrer");
      return;
    }
    nav(cleanUrl);
  };

  const handleUploadDoc = () => {
    setSelectedDocName("document");
    fileRef.current?.click();
  };

  const { user } = useAuth();
  const [ru] = useState<RootUser | null>(null);
  const displayName =
    user?.displayName || user?.email?.split("@")[0] || ru?.profile?.company_name || "User";

  const onPickedFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
    if (!selectedDocName) setSelectedDocName("document");
    setShowDocPopup(true);
    e.target.value = "";
  };

  const handleDocumentSubmit = async (name: string, expiry: string) => {
    if (!pendingFile) return;
    if (!expiry) { toast.error("Please select an expiry date"); return; }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (new Date(expiry) < today) { toast.error("Expiry date cannot be in the past"); return; }
    try {
      setUploading(true);
      await uploadProfileDocument(pendingFile, name, expiry);
      toast.success("Document uploaded");
      const updatedDocs = await getUploadProfileDocument();
      setDocuments(updatedDocs || []);
      setShowDocPopup(false);
      setPendingFile(null);
      setSelectedDocName(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message ||  e?.response?.data?.detail || "Upload failed, please try again.");
    } finally { setUploading(false); }
  };

  const resumeDraft = async (savedId?: number | string) => {
    try {
      if (!savedId) { nav("/dashboard/write"); return; }
      await fetch(`/grant-writing/qa/full-details/?saved_grant_id=${encodeURIComponent(String(savedId))}`, { method: "GET", credentials: "include" });
      nav(`/dashboard/write/${savedId}`);
    } catch { nav(`/dashboard/write/${savedId}`); }
  };

  return (
    <div className="max-w-[1180px] mx-auto px-5 py-6">
      {/* Page header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-[28px] md:text-[32px] font-semibold text-[#141414] leading-tight">Welcome back</h1>
          <p className="text-[13px] text-[#6B7280] -mt-[2px]">Here is the overview of your recent activities</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 cursor-pointer p-1" onClick={() => nav("/dashboard/track")}>
            <span className="material-icons bg-primary rounded-full text-[18px] p-[6px]">calendar_month</span>
            <span className="hover:underline text-sm">View Deadline</span>
          </div>
          <button className="flex items-center gap-2 text-sm border bg-primary rounded-lg p-2" onClick={() => nav("/dashboard/discover")}>
            <span className="material-icons bg-primary rounded-full">add</span>
            <span>New Application</span>
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-5">
        {/* ── LEFT COLUMN ── */}
        <div className="flex flex-col gap-5">

          {/* SECTION 1: Stats */}
          {statsLoading ? <StatsRowSkeleton /> : (
            <StatsRow
              totalApplied={totalApplied}
              appliedDelta={appliedDelta}
              inProgress={inProgress}
              inProgressDelta={inProgressDelta}
              totalAvailable={totalAvailable}
              availableDelta={availableDelta}
            />
          )}

          {/* SECTION 2: Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            {/* Top picks */}
            {picksLoading ? <SkeletonCard lines={3} /> : (
              <Card>
                <div className="p-4">
                  <div className="text-xl font-semibold text-[#141414]">Top picks for {displayName}</div>
                  <div className="text-[15px]">Based on your profile &amp; past activity</div>
                  {picks.length ? (
                    picks.map((p, i) => (
                      <div key={i} className="flex justify-between items-start mt-4">
    <div className="space-y-1">
    <div className="text-[13px] md:text-[14px] text-[#141414] font-semibold">
      {p.title}
      {p.range_label && (
        <span className="font-normal"> - {p.range_label}</span>
      )}
    </div>

    <button
      className="text-[12px] text-[#0077B6] hover:underline"
      onClick={() => handleTopPickClick(p.url)}
    >
      Explore
    </button>

    {p.badge && (
      <Badge
        text={p.badge}
        tone={
          p.badge === "High Match"
            ? "success"
            : p.badge === "New"
            ? "info"
            : "default"
        }
      />
    )}
  </div>

  <button
    className="flex-shrink-0 text-[11px] font-semibold px-3 py-1 rounded-full bg-primary text-white"
    onClick={() => nav("/dashboard/discover")}
  >
    View all
  </button>
</div>
                    ))
                  ) : (
                    <div className="text-[13px] md:text-[14px] text-[#6B7280] py-4">No picks yet</div>
                  )}
                </div>
              </Card>
            )}

            {/* Upcoming dues */}
            <UpcomingDuesCard
              dues={upcomingDues}
              loading={duesLoading}
              onNavigate={resumeDraft}
            />

            {/* Next steps */}
            {stepsLoading ? <SkeletonCard lines={2} /> : (
              <Card>
                <div className="p-4">
                  <div className="text-xl font-semibold">Next step</div>
                  <div className="text-[15px]">Stay on track with your priority action</div>
                  {steps.length ? (
                    <div>
                      <div className="mt-4 space-y-4">
                        {steps.map((s, i) => {
                          const savedId = (s as any).saved_id ?? (s as any).saved_grant_id ?? (s as any).savedGrantId ?? null;
                          return (
                            <div key={i}>
                              <div className="flex items-center justify-between">
                                <div className="text-[13px] md:text-[14px] text-[#141414] font-semibold pr-3">{s.title}</div>
                                {s.action && (
                                  <button className="text-[12px] text-[#0077B6] hover:underline whitespace-nowrap"
                                    onClick={() => resumeDraft(savedId)}>
                                    {s.action}
                                  </button>
                                )}
                              </div>
                              <div className="mt-2 h-2 rounded-full bg-[#E5E7EB]">
                                <div className="h-2 rounded-full" style={{ width: `${Math.min(100, Math.max(0, s.progress_pct || 0))}%`, background: "#93C893" }} />
                              </div>
                              {s.progress_pct != null && (
                                <div className="text-[12px] text-[#6B7280] mt-1">{Math.round(s.progress_pct)}% completed</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="pt-4 text-right">
                        <button className="text-[#141414] text-[13px] font-medium hover:underline"
                          onClick={() => nav("/dashboard/organize")}>View all</button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-[13px] text-[#6B7280] py-4">No next steps available</div>
                  )}
                </div>
              </Card>
            )}

            {/* Community Hub */}
            {communityLoading ? <SkeletonCard lines={3} /> : (
              <Card>
                <div className="p-4">
                  <div className="text-xl font-semibold">Community Hub</div>
                  <div className="text-[15px]">Connect, ask &amp; share with founders like you</div>
                  {threads.length ? (
                    <div>
                      <div className="mt-4 space-y-4">
                        {threads.map((t, i) => (
                          <div key={t.id ?? i} className="flex items-start justify-between">
                            <div className="pr-3">
                              <div className="text-[13px] md:text-[14px] text-[#141414] font-semibold">
                                {t.topic ?? t.title}
                                {typeof t.replies === "number" && t.replies > 0 && (
                                  <span className="text-[12px] text-[#6B7280] font-normal"> — {t.replies} replies</span>
                                )}
                                {typeof t.likes === "number" && t.likes > 0 && (
                                  <span className="text-[12px] text-[#6B7280] font-normal"> — {t.likes} likes</span>
                                )}
                              </div>
                            </div>
                            <button className="text-[12px] text-[#0077B6] hover:underline whitespace-nowrap">
                              {t.action || "Open"}
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="pt-4 text-right">
                        <button className="text-[#141414] text-[13px] font-medium hover:underline">Resume reading</button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-[13px] text-[#6B7280] py-4">No community threads yet</div>
                  )}
                </div>
              </Card>
            )}

            {/* Charts section */}
            <div className="flex gap-4 flex-col">
              <Card>
                <div className="p-4 text-center">
                  <div className="text-2xl font-semibold text-[#141414]">Grant Progress Metrics</div>
                  <div className="text-[16px] mt-[2px]">Overview of grant submissions and progress.</div>
                </div>
              {/* </Card> */}
              {chartsLoading ? (
                <div className="rounded-2xl border border-black/10 bg-white p-4">
                  <SkeletonBox className="h-4 w-1/2 mb-4" />
                  <div className="flex items-end gap-2 h-32">
                    {[60, 45, 80, 35, 70, 55, 90].map((_, i) => (
                      <SkeletonBox key={i} className="flex-1 rounded" />
                    ))}
                  </div>
                </div>
              ) : (
                <MonthlyBarsCard
                  title="Monthly Grant Applied"
                  subtitle="Number of Grants"
                  data={
                    monthlySubmissions?.length
                      ? monthlySubmissions.map((m) => ({ month: m.month, count: m.count }))
                      : ([
                          { month: "Jan", count: 84 }, { month: "Feb", count: 72 }, { month: "Mar", count: 36 },
                          { month: "Apr", count: 58 }, { month: "May", count: 88 }, { month: "Jun", count: 64 },
                          { month: "Jul", count: 22 }, { month: "Aug", count: 80 }, { month: "Sep", count: 56 },
                          { month: "Oct", count: 44 }, { month: "Nov", count: 74 }, { month: "Dec", count: 60 },
                        ] as MonthlyBar[])
                  }
                />
              )}
              </Card>
            </div>

            {chartsLoading ? <SkeletonCard lines={4} /> : (
              <TotalGrantsByFeaturesCard
                total={grantDistribution?.total_grants ?? totalAvailable}
                features={(grantDistribution?.features ?? []).map((f) => ({ name: f.name, pct: f.pct }))}
              />
            )}
          </div>
        </div>

        {/* ── RIGHT SIDEBAR ── */}
        <div className="grid gap-5 lg:sticky lg:top-4 lg:self-start">
          <div className="flex flex-col gap-5">
            <AiInsightsCard ai={ai} aiLoading={aiLoading} aiErr={aiErr} onRefresh={loadAi} />

            {/* Update Profile */}
            <Card>
              <div className="p-4">
                <div className="text-xl font-semibold text-[#141414]">Update Profile</div>
                <div className="mt-2 grid gap-2">
                  <div onClick={() => setShowTeamModal(true)}
                    className="flex gap-2 items-center py-1 hover:bg-black/[0.02] cursor-pointer">
                    <span className="material-icons text-md border bg-primary rounded-full p-1 font-medium">add</span>
                    <span className="text-sm">Add team member</span>
                  </div>
                  <div onClick={handleUploadDoc}
                    className="flex gap-2 items-center py-1 hover:bg-black/[0.02] cursor-pointer">
                    <span className="material-icons text-md border bg-primary rounded-full p-1">upload</span>
                    <span className="text-sm">Upload Document</span>
                  </div>
                  <input ref={fileRef} type="file" className="hidden" onChange={onPickedFile} accept="application/pdf,image/*" />
                </div>
                {documents.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs text-[#6B7280] mb-1">Uploaded documents</div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {documents.map((doc) => (
                        <div key={doc.id} className="flex flex-col text-[12px] text-[#111] mb-3">
                          <span className="font-medium mb-1">{doc.name}</span>
                          {doc.files?.length ? (
                            doc.files.map((file: any) => (
                              <div key={file.id} className="flex items-center justify-between ml-2 mb-1">
                                <span className="truncate mr-2">File {file.id}</span>
                                <a href={file.file} target="_blank" rel="noreferrer"
                                  className="text-[#0077B6] hover:underline whitespace-nowrap">View</a>
                              </div>
                            ))
                          ) : (
                            <span className="text-gray-400 ml-2">No files uploaded</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Join Community */}
            <div className="rounded-2xl p-4 text-white"
              style={{ background: "linear-gradient(180deg,#9DDA9D 0%, #4BAE4B 100%)" }}>
              <div className="text-[15px] font-semibold">Join Community</div>
              <div className="text-[13px] opacity-95 mt-1 mb-3">Connect, share and learn with fellow grant writers</div>
              <button onClick={() => nav("/dashboard/community")}
                className="inline-flex items-center gap-2 bg-white text-[#17331F] rounded-lg px-3 py-2 text-[13px] font-medium hover:brightness-95">
                <span className="material-icons text-[18px]">groups</span>
                Connect
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <DocumentPopup
        show={showDocPopup}
        selectedDocName={selectedDocName}
        pendingFile={pendingFile}
        uploading={uploading}
        onClose={() => { setShowDocPopup(false); setSelectedDocName(null); setPendingFile(null); }}
        onSubmit={handleDocumentSubmit}
        onFileChange={(file) => setPendingFile(file)}
      />
      <TeamMemberModal open={showTeamModal} onClose={() => setShowTeamModal(false)} />
    </div>
  );
}
